import { useQuery } from "@apollo/client";
import { Tab, Typography } from "@mui/material";
import { uniq } from "lodash";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Link,
    Outlet,
    useLocation,
    useParams,
    useNavigate,
} from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import {
    GetLegacyNextAssetQuery,
    GetLegacyNextAssetQueryVariables,
    InsightsAsset,
    InsightsPropertyTree,
} from "graphql-types/graphql";
import { AssetAvatar, PageLoading } from "legacyNext/components";
import { AssetStoreProvider } from "legacyNext/store/asset-page-store";

import { S } from "./asset-page-layout.styles";
import { AssetProfile } from "./AssetProfile";
import { Benchmarks } from "../Benchmarks";
import { getAssetBuildings, flat } from "../Overview/overview.utils";
import { GET_ASSET } from "../queries";

export const AssetPageLayout = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const contentRef = useRef<HTMLDivElement>(null);
    const handlePrint = useReactToPrint({
        contentRef,
    });

    const tabs = [
        {
            label: t("legacyNext.masterData", "master data"),
            path: "",
        },
        {
            label: t("legacyNext.consumption", "consumption"),
            path: "consumption",
        },
        {
            label: t("legacyNext.sustainability", "sustainability"),
            path: "sustainability",
        },
        {
            label: t("legacyNext.economy", "economy"),
            path: "economy",
        },
    ];

    const location = useLocation();
    const [tabValue, setTabValue] = useState(() => {
        const path = location.pathname.split("/").pop();
        const index = tabs.findIndex((tab) => tab.path === path);
        return index !== -1 ? index : 0;
    });

    const { data, loading } = useQuery<
        GetLegacyNextAssetQuery,
        GetLegacyNextAssetQueryVariables
    >(GET_ASSET, { variables: { id: id ?? "" } });

    if (loading) {
        return <PageLoading />;
    }

    const asset = data?.getLegacyNextAsset as InsightsAsset;

    if (!asset) return null;

    const buildings = getAssetBuildings(
        asset.properties as InsightsPropertyTree[]
    );

    const [building] = buildings || asset.properties;
    const [address1, address2] =
        asset.name?.split(", ") || asset.properties[0].fullAddress.split(", ");

    const onTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleBackClick = () => {
        navigate(-1);
    };

    return (
        <AssetStoreProvider
            assetId={asset.id ?? ""}
            assetBfe={asset.externalId ?? ""}
            crremCategories={uniq(
                flat(asset.properties).map((e) => e.crremPropertyType)
            )}
        >
            <S.Page ref={contentRef}>
                <S.Panel>
                    <S.NavigationLink to="#" onClick={handleBackClick}>
                        <S.ArrowBack />
                        <S.NavigationText>
                            {t("legacyNext.back", "back")}
                        </S.NavigationText>
                    </S.NavigationLink>
                    <S.NavigationButton onClick={(e) => handlePrint()}>
                        <S.NavigationText>
                            {t("common.download", "download PDF")} PDF
                        </S.NavigationText>
                        <S.DownloadIcon />
                    </S.NavigationButton>
                </S.Panel>
                <S.Header>
                    <S.AssetTitle>
                        <Typography variant="h1">{`${address1},`}</Typography>
                        <Typography variant="h2">{address2}</Typography>
                    </S.AssetTitle>
                    <S.HeaderInner>
                        <AssetAvatar
                            latitude={building.latitude}
                            longitude={building.longitude}
                        />
                        <AssetProfile
                            area={asset.area ?? null}
                            assetExternalId={asset.externalId}
                            building={building}
                        />
                    </S.HeaderInner>
                </S.Header>

                <S.Content>
                    <S.Section>
                        <Benchmarks assetId={asset.externalId ?? ""} />
                    </S.Section>
                    <S.Tabs value={tabValue} onChange={onTabChange}>
                        {tabs.map((tab, index) => (
                            <Tab
                                key={index}
                                tabIndex={index}
                                label={tab.label}
                                component={Link}
                                to={tab.path}
                            />
                        ))}
                    </S.Tabs>

                    <Outlet context={{ asset }} />
                </S.Content>
            </S.Page>
        </AssetStoreProvider>
    );
};
